"use client"

import { useState } from "react"
import { cn } from "@/lib/utils"
import { useLanguage } from "@/lib/i18n/language-provider"

interface FuturisticMessageProps {
  onActivate: () => void
  message?: string
  subMessage?: string
  actionText?: string
}

export function FuturisticMessage({ onActivate, message, subMessage, actionText }: FuturisticMessageProps) {
  const [isHovered, setIsHovered] = useState(false)
  const { t } = useLanguage()

  return (
    <div className="relative max-w-2xl mx-auto">
      {/* Glowing background effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-red-600/20 via-red-500/10 to-black/20 blur-3xl rounded-3xl" />

      {/* Main container */}
      <div className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 border border-red-500/30 rounded-2xl p-8 shadow-2xl backdrop-blur-sm">
        {/* Animated border glow */}
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-red-500/50 via-transparent to-red-600/50 opacity-0 animate-pulse" />

        <div className="mb-6 text-center">
          <h1 className="text-4xl font-bold text-transparent bg-gradient-to-r from-red-400 to-red-600 bg-clip-text">
            {message || t("preview.disabledTitle")}
          </h1>
        </div>

        {/* Content message */}
        <div className="mb-8 space-y-4">
          <p className="text-gray-300 leading-relaxed text-lg">
            {subMessage || t("preview.disabledMessage")}
          </p>
        </div>

        <div className="flex justify-center">
          <button
            onClick={onActivate}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={cn(
              "relative group px-8 py-4 text-white font-semibold rounded-xl",
              "shadow-lg transition-all duration-300 ease-out",
              "hover:scale-105 hover:shadow-xl active:scale-95 active:shadow-md",
              "bg-gradient-to-br from-red-500 via-red-700 to-black border-red-600/70",
              "hover:from-red-400 hover:via-red-600 hover:to-red-800 hover:shadow-red-600/50",
            )}
          >
            {/* Button glow effect */}
            <div
              className={cn(
                "absolute inset-0 rounded-xl blur-md transition-opacity duration-300",
                "bg-gradient-to-r from-red-200/40 to-red-800/40",
                isHovered ? "opacity-100" : "opacity-0",
              )}
            />

            {/* Animated background particles */}
            <div className="absolute inset-0 rounded-xl overflow-hidden">
              <div
                className={cn(
                  "absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent",
                  "transform -skew-x-12 transition-transform duration-700",
                  isHovered ? "translate-x-full" : "-translate-x-full",
                )}
              />
            </div>

            {/* Button text */}
            <span className="relative z-10 flex items-center gap-1">
              {actionText || t("preview.activate")}
              <svg
                className={cn("w-5 h-5 transition-transform duration-300", isHovered ? "translate-x-1" : "")}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>

            {/* Plasma Core specific effect */}
          </button>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-4 right-4 w-2 h-2 bg-red-500 rounded-full animate-pulse" />
        <div className="absolute bottom-4 left-4 w-1 h-1 bg-red-400 rounded-full animate-pulse delay-500" />
      </div>
    </div>
  )
}
